import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DownloadExcelButton, DrilldownDialog, NewReactTable as TableComponent } from '../ui/index.jsx';
import { Tabs, Tab, Box, Autocomplete, TextField, Typography, Tooltip, CircularProgress, IconButton } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';

import { request, methods, isRequestCancelled } from '../../../../utils/http-common.js';
import useCancellableRequest from '../../../../hooks/useCancellableRequest.js';
import { formatNumberWithCommas } from '../../../../helpers/numericHelpers.js';
import MTDChart from './MTDChart/index.jsx';
import AggregatedView from './AggregatedView/index.jsx';
import InsightsView from './Insights/index.jsx';
import WMAPEView from './WMAPEView/index.jsx';
import { SalesForecastTableSkeleton } from '../common/ModulePageSkeleton/ModulePageSkeleton.jsx';
import { renderSensitiveCellValue } from '../common/sensitiveFields.jsx';

const PAGE_SIZE = 50;

const RATING_COLORS = {
  P90: { bg: '#e8f5e9', color: '#2e7d32' },
  P70: { bg: '#fff8e1', color: '#f57f17' },
  P50: { bg: '#fce4ec', color: '#c62828' },
};

const shimmerKeyframes = `
  @keyframes shimmer {
    0%   { background-position: 200% 0 }
    100% { background-position: -200% 0 }
  }
`;

// Spread dealer-analysis columns across the ~85vw modal (react-table block layout uses fixed px widths)
const getDealerAnalysisColWidth = (columnCount, key) => {
  const count = Math.max(columnCount, 1);
  const modalInnerWidth = typeof window !== 'undefined' ? window.innerWidth * 0.85 - 64 : 1100;
  const base = Math.max(100, Math.floor(modalInnerWidth / count));
  if (key === 'month') return base + 28;
  return base;
};

// Transform dealer analysis row_header into TableComponent column definitions
const changeKeysOfHeader = (data) => {
  const columnCount = data?.length || 1;
  return data?.map((item) => {
    const defaultCell = (props) => {
      const raw = props.row.original[item.key];
      const isNumber =
        typeof raw === 'number' ||
        props.row.original.data_type === 'number';
      return (
        <Box display="flex" justifyContent={isNumber ? 'center' : 'left'}>
          <span className={props.row.original['class']}>
            {isNumber
              ? formatNumberWithCommas(raw)
              : renderSensitiveCellValue(item.key, raw)}
          </span>
        </Box>
      );
    };

    if (item.sub_headers && item.sub_headers.length > 0) {
      return {
        ...item,
        Header: item.label,
        id: item.key,
        width: getDealerAnalysisColWidth(columnCount, item.key),
        columns: item.sub_headers.map((sub) => ({
          Header: () => (
            <span className={`flex ${sub.data_type === 'number' ? 'jc-center' : ''}`}>
              {sub.sub_label != '' && sub.sub_label != '0' ? (
                <span>{sub.label} <br /> {sub.sub_label}</span>
              ) : (
                <span>{sub.label}</span>
              )}
            </span>
          ),
          id: sub.key,
          width: getDealerAnalysisColWidth(columnCount, sub.key),
          ...sub,
          Cell: (props) => {
            const value = props.row.original[sub.key];
            const isNumber = typeof value === 'number' || sub.data_type === 'number';
            return (
              <Box display="flex" justifyContent={isNumber ? 'center' : 'left'}>
                <span className="pointer">
                  {isNumber ? formatNumberWithCommas(value) : renderSensitiveCellValue(sub.key, value)}
                </span>
              </Box>
            );
          },
        })),
      };
    }

    return {
      ...item,
      width: getDealerAnalysisColWidth(columnCount, item.key),
      Header: item.label,
      id: item.key,
      Cell: defaultCell,
    };
  }) || [];
};

// Transform dealer analysis row_data: map sub_rows → subRows for TableComponent
const newChangeKeysOfRow = (data) => {
  if (data && data.length > 0) {
    return data.map((item, i) => {
      if (item.sub_rows) {
        item['subRows'] = item.sub_rows;
        newChangeKeysOfRow(item.sub_rows);
      } else {
        item['subRows'] = [];
      }
      item['_myId'] = new Date().getUTCMilliseconds() + i + Math.random() * 902129120;
      return item;
    });
  } else return [];
};

const HEADER_BG = {
  p90_forecast: RATING_COLORS.P90.color,
  p70_forecast: RATING_COLORS.P70.color,
  p50_forecast: RATING_COLORS.P50.color,
};

const buildColumns = (headers, columnSums, totalsLoading, onDealerClick) =>
  headers.filter((h) => h.key !== 'forecast_rating').map((h) => {
    const isNumeric = h.data_type === 'number' || h.key === 'dealer_code';

    const headerFn = () => (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2', gap: 2 }}>
        <span style={{ fontSize: '11px' }}>{h.label}</span>
        {isNumeric && (
          totalsLoading ? (
            <span style={{
              display: 'block',
              width: 48,
              height: 8,
              borderRadius: 3,
              background: 'linear-gradient(90deg, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.2) 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }} />
          ) : columnSums[h.key] ? (
            <span style={{
              fontWeight: 600,
              fontSize: '10px',
              lineHeight: 1.2,
              color: '#ffffff',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              padding: '1px 6px',
              borderRadius: '8px',
            }}>
              {columnSums[h.key]}
            </span>
          ) : null
        )}
      </span>
    );

    const base = {
      key: h.key,
      id: h.key,
      label: h.label,
      Header: headerFn,
      data_type: h.data_type,
      sortable: h.sortable ?? false,
      filterable: h.filterable ?? false,
      width: h.key === 'dealer' ? 220 : h.key === 'upside_downside_vol' ? 160 : 110,
      ...(HEADER_BG[h.key] ? { headerStyle: { background: HEADER_BG[h.key] } } : {}),
    };

    if (h.key === 'upside_downside_vol') {
      return {
        ...base,
        Cell: ({ row }) => {
          const val = row.original.upside_downside_vol;
          if (val === '—' || val === undefined || val === null) {
            return <span style={{ color: '#aaa' }}>—</span>;
          }
          const raw = row.original.upside_downside_raw;
          const color = raw > 0 ? '#2e7d32' : raw < 0 ? '#c62828' : '#555';
          const prefix = raw > 0 ? '+' : '';
          return (
            <Tooltip title="Forecast vs avg of last 2 months" arrow>
              <span style={{ color, fontWeight: 600 }}>
                {prefix}{typeof val === 'number' ? formatNumberWithCommas(Math.round(val * 100) / 100) : val}
              </span>
            </Tooltip>
          );
        },
      };
    }

    if (h.key === 'dealer_code') {
      return {
        ...base,
        Cell: ({ row }) => {
          const val = row.original[h.key];
          return val ? (
            <span
              style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => onDealerClick(val)}
            >
              {val}
            </span>
          ) : (
            <span style={{ color: '#aaa' }}>—</span>
          );
        },
      };
    }

    if (h.key === 'dealer_status') {
      return {
        ...base,
        Cell: ({ row }) => {
          const val = row.original[h.key];
          if (!val) return <span style={{ color: '#aaa' }}>—</span>;
          const isChurned = val === 'Churned';
          return (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 600,
                background: isChurned ? '#ffebee' : '#e8f5e9',
                color: isChurned ? '#c62828' : '#2e7d32',
              }}
            >
              {val}
            </span>
          );
        },
      };
    }

    if (h.sub_headers && h.sub_headers.length > 0) {
      return {
        ...h,
        Header: h.label,
        id: h.key,
        width: 110,
        columns: h.sub_headers.map((sub) => ({
          Header: () => (
            <span className={`flex jc-center`}>
              {sub.sub_label != '' && sub.sub_label != '0' ? (
                <span>{sub.label} <br /> {sub.sub_label}</span>
              ) : (
                <span>{sub.label}</span>
              )}
            </span>
          ),
          id: sub.key,
          width: 110,
          ...sub,
          Cell: (props) => {
            const value = props.row.original[sub.key];
            const isNumber = typeof value === 'number' || sub.data_type === 'number';
            return (
              <Box display="flex" justifyContent={isNumber ? 'center' : 'left'}>
                <span className="pointer">
                  {isNumber ? formatNumberWithCommas(value) : renderSensitiveCellValue(sub.key, value)}
                </span>
              </Box>
            );
          },
        })),
      };
    }

    if (isNumeric) {
      return {
        ...base,
        Cell: ({ row }) => {
          const val = row.original[h.key];
          if (val === null || val === undefined || val === '') {
            return <span className='flex jc-center' style={{ color: '#aaa' }}>—</span>;
          }
          return <span className='flex jc-center'>{typeof val === 'number' ? formatNumberWithCommas(Math.round(val * 100) / 100) : val}</span>;
        },
      };
    }

    return {
      ...base,
      Cell: ({ row }) => (
        <span>{renderSensitiveCellValue(h.key, row.original[h.key], { empty: '—' })}</span>
      ),
    };
  });

const MONTH_ABBR_INDEX = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

const ForecastReport = () => {
  const { request: cancellableRequest } = useCancellableRequest('sales-pnf/forecast-report/main-table');
  const { request: modalCancellableRequest } = useCancellableRequest('sales-pnf/forecast-report/dealer-modal');

  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loadingMonths, setLoadingMonths] = useState(true);

  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [disableTab, setDisableTab] = useState(false);
  const [loading, setLoading] = useState(false);

  // Totals (column sums) — loaded separately and only on initial load / filter+sort change
  const [columnSums, setColumnSums] = useState({});
  const [totalsLoading, setTotalsLoading] = useState(false);

  // Snapshot days per month (from getforecastreportmonths)
  const [snapshotDaysMap, setSnapshotDaysMap] = useState({});

  // Dealer analysis modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDealerCode, setModalDealerCode] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalHeaders, setModalHeaders] = useState([]);
  const [modalRows, setModalRows] = useState([]);
  const [modalExporting, setModalExporting] = useState(false);

  const currentPageRef = useRef(1);
  const currentTotalFetchedRef = useRef(0);
  const activeSortRef = useRef([]);
  const activeFiltersRef = useRef([]);

  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const tabValue = searchParams.get('tab-second') || 'forecast-report';

  useEffect(() => {
    if (tabValue === 'forecast-report') {
      setDisableTab(isFetching);
    }
  }, [isFetching, tabValue]);

  // --- Fetch available forecast months ---
  useEffect(() => {
    const fetchMonths = async () => {
      setLoadingMonths(true);
      let fetchError;
      try {
        let res;
        [fetchError, res] = await cancellableRequest('salespnf/getforecastreportmonths', methods.POST, {}, { cancelKey: 'months' });
        if (isRequestCancelled(fetchError)) return;
        if (res?.data) {
          const monthFilter = res.data.find((f) => f.key === 'month');
          const opts = monthFilter?.value || [];
          setMonths(opts);
          if (opts.length > 0) {
            const now = new Date();
            const nowKey = now.getFullYear() * 100 + (now.getMonth() + 1);
            const toKey = (o) => {
              const [mmm, yy] = (o?.value || '').split('-');
              return (2000 + parseInt(yy, 10)) * 100 + MONTH_ABBR_INDEX[mmm];
            };
            const current = opts.find((o) => toKey(o) === nowKey)
              ?? opts.find((o) => toKey(o) <= nowKey)
              ?? opts[opts.length - 1];
            setSelectedMonth(current);
          }
          const sdFilter = res.data.find((f) => f.key === 'snapshot_days');
          if (sdFilter?.value?.[0]) setSnapshotDaysMap(sdFilter.value[0]);
        }
      } catch (e) {
        if (!isRequestCancelled(e)) {
          console.error('Failed to fetch forecast months', e);
        }
      } finally {
        if (!isRequestCancelled(fetchError)) {
          setLoadingMonths(false);
        }
      }
    };
    fetchMonths();
  }, []);

  // --- Paginated row data fetch ---
  const fetchData = useCallback(async (page, sortBy, filters, month) => {
    if (!month) return;
    setIsFetching(true);
    let fetchError;
    try {
      const payload = {
        month: month.value,
        page_number: page,
        page_size: PAGE_SIZE,
        sort_by: sortBy || [],
        filters: filters || [],
      };
      let res;
      [fetchError, res] = await cancellableRequest('salespnf/getforecastreportv3', methods.POST, payload, { cancelKey: 'data' });
      if (isRequestCancelled(fetchError)) return;
      if (res?.data) {
        const rowHeader = res.data.row_header || [];
        const rowData = res.data.row_data || [];
        const total = res.data.total || 0;

        setTotalRecords(total);
        // Only update headers from the data endpoint (no sub_labels here)
        if (page === 1) setHeaders(rowHeader);

        if (page === 1) {
          setRows(rowData);
          currentTotalFetchedRef.current = rowData.length;
        } else {
          setRows((prev) => [...prev, ...rowData]);
          currentTotalFetchedRef.current += rowData.length;
        }
        currentPageRef.current = page;
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Failed to fetch forecast report', e);
      }
    } finally {
      if (!isRequestCancelled(fetchError)) {
        setIsFetching(false);
        setLoading(false);
      }
    }
  }, [cancellableRequest]);

  // --- Column totals fetch (separate, async) ---
  const fetchTotals = useCallback(async (month, sortBy, filters) => {
    if (!month) return;
    setTotalsLoading(true);
    let fetchError;
    try {
      let res;
      [fetchError, res] = await cancellableRequest('salespnf/getforecastreporttotalsv3', methods.POST, {
        month: month.value,
        sort_by: sortBy || [],
        filters: filters || [],
      }, { cancelKey: 'totals' });
      if (isRequestCancelled(fetchError)) return;
      if (res?.data?.row_header) {
        const sums = {};
        res.data.row_header.forEach((h) => {
          if (h.sub_label && h.sub_label !== '' && h.sub_label !== '0') {
            sums[h.key] = formatNumberWithCommas(Math.round(parseFloat(h.sub_label) * 100) / 100);
          }
        });
        setColumnSums(sums);
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Failed to fetch forecast totals', e);
      }
    } finally {
      if (!isRequestCancelled(fetchError)) {
        setTotalsLoading(false);
      }
    }
  }, [cancellableRequest]);

  // On month change: fire both in parallel, reset state
  useEffect(() => {
    setLoading(true);
    if (!selectedMonth) return;
    currentPageRef.current = 1;
    currentTotalFetchedRef.current = 0;
    activeSortRef.current = [];
    activeFiltersRef.current = [];
    setRows([]);
    setColumnSums({});
    fetchData(1, [], [], selectedMonth);
    fetchTotals(selectedMonth, [], []);
  }, [selectedMonth, fetchData, fetchTotals]);

  // --- scrollFetch: called by TableComponent ---
  const scrollFetch = useCallback(
    (scrollEvent, sortList, filterList) => {
      if (scrollEvent) {
        // Scroll to bottom — load next page only (no totals refetch)
        const { scrollHeight, scrollTop, clientHeight } = scrollEvent;
        if (
          scrollHeight - scrollTop - clientHeight < 10 &&
          !isFetching &&
          currentTotalFetchedRef.current < totalRecords
        ) {
          fetchData(currentPageRef.current + 1, activeSortRef.current, activeFiltersRef.current, selectedMonth);
        }
      } else {
        // Sort or filter changed — reset rows and refetch both data + totals in parallel
        activeSortRef.current = sortList || [];
        activeFiltersRef.current = filterList || [];
        currentPageRef.current = 1;
        currentTotalFetchedRef.current = 0;
        // setRows([]);
        setColumnSums({});
        fetchData(1, activeSortRef.current, activeFiltersRef.current, selectedMonth);
        fetchTotals(selectedMonth, activeSortRef.current, activeFiltersRef.current);
      }
    },
    [isFetching, totalRecords, selectedMonth, fetchData, fetchTotals],
  );

  // --- Export ---
  const handleExport = async () => {
    if (!selectedMonth || isExporting) return;
    setIsExporting(true);
    try {
      const payload = {
        month: selectedMonth.value,
        sort_by: activeSortRef.current,
        filters: activeFiltersRef.current,
        export: 'true',
      };
      const [, response] = await request('salespnf/getforecastreportv3', methods.POST, payload, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers?.['content-disposition'] || '';
      a.download = disposition.split('=')[1] || `Forecast_Report_${selectedMonth.value}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setIsExporting(false);
    }
  };

  // --- Dealer analysis modal ---
  const handleDealerClick = async (dealerCode) => {
    setModalDealerCode(dealerCode);
    setModalOpen(true);
    setModalLoading(true);
    setModalHeaders([]);
    setModalRows([]);
    let fetchError;
    try {
      let res;
      [fetchError, res] = await modalCancellableRequest('salespnf/dealer-analysis/gettabel', methods.POST, {
        dealer_code: dealerCode,
        dealer_segment: 'Total',
      }, { cancelKey: 'dealer-table' });
      if (isRequestCancelled(fetchError)) return;
      if (res?.data) {
        setModalHeaders(changeKeysOfHeader(res.data.row_header || []));
        setModalRows(newChangeKeysOfRow(res.data.row_data || []));
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Failed to fetch dealer analysis', e);
      }
    } finally {
      if (!isRequestCancelled(fetchError)) {
        setModalLoading(false);
      }
    }
  };

  const handleModalExport = async () => {
    if (modalExporting) return;
    setModalExporting(true);
    try {
      const [, response] = await request('salespnf/dealer-analysis/gettabel', methods.POST, {
        dealer_code: modalDealerCode,
        dealer_segment: 'Total',
        export: 'true',
      }, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers?.['content-disposition'] || '';
      a.download = disposition.split('=')[1] || `Dealer_Analysis_${modalDealerCode}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Modal export failed', e);
    } finally {
      setModalExporting(false);
    }
  };

  const columns = buildColumns(headers, columnSums, totalsLoading, handleDealerClick);

  const filterSelectSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 6, fontSize: 12 },
  };

  let tabs = [
    { value: 'forecast-report', label: 'Monthly Forecast Report' },
    { value: 'mtd-chart', label: 'MTD Chart' },
    { value: 'dealer-segment', label: 'Dealer Segment' },
    { value: 'dealer-risk', label: 'Dealer Risk' },
    { value: 'account-owner', label: 'Account Owner' },
    { value: 'account-owner-risk', label: 'Account Owner Risk' },
    { value: 'gyr-risk', label: 'GYR Risk' },
    { value: 'product-category', label: 'Product Category' },
    { value: 'insights', label: 'Insights' },
    { value: 'wmape', label: 'WMAPE' },
  ];

  const handleTabChange = (_, newValue) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', 'forecast-report');
    newParams.set('tab-second', newValue);
    history.replace({
      pathname: location.pathname,
      search: `?${newParams.toString()}`,
    });
  };

  const handleDisableTab = useCallback((param) => {
    setDisableTab(param);
  }, []);

  return (
    <Box pt={1} className="monthly-forecast-tracker">
      {/* Shimmer keyframe — injected once */}
      <style>{shimmerKeyframes}</style>

      {/* Header row — hidden on WMAPE (month filter + export live inside WMAPEView) */}
      {tabValue !== 'wmape' && (
        <Box display="flex" alignItems="center" gap={1} mb={1} className="monthly-forecast-tracker__toolbar">
          <Box width={140}>
            <Autocomplete
              size="small"
              options={months}
              value={selectedMonth}
              loading={loadingMonths}
              getOptionLabel={(opt) => opt?.label || ''}
              onChange={(_, v) => setSelectedMonth(v)}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Month" variant="outlined" sx={filterSelectSx} />
              )}
            />
          </Box>

          <Box flex={1} />
          {tabValue === 'forecast-report' && (
            <DownloadExcelButton onClick={handleExport} loading={isExporting} />
          )}
        </Box>
      )}

      {/* Tabs */}
      <Box
        sx={{ display: "flex", justifyContent: "start" }}
        mb={2}
        mt={1}
        width="100%"
        className="tabs"
      >
        <Tabs
          className="monthly-forecast-tracker__sub-tabs"
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          TabIndicatorProps={{ style: { backgroundColor: "#062565" } }}
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 400,
              fontSize: "12px",
              px: 1.5,
              py: 1,
              color: "#666",
              minHeight: 40,
            },
            "& .Mui-selected": {
              color: "#062565",
              fontWeight: 600,
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab disabled={disableTab} key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>
      </Box>

      <Box className="monthly-forecast-tracker__content">
        {tabValue === "forecast-report" && (
          loadingMonths || loading ? (
            <SalesForecastTableSkeleton />
          ) : (
            <div className="jklc-table-wrapper forecast-report-table">
              <TableComponent
                columns={columns}
                data={rows}
                hidePagination
                isFetching={isFetching}
                totalRecords={totalRecords}
                scrollFetch={scrollFetch}
                stickyColumnCount={2}
                handleDisableTab={handleDisableTab}
              />
            </div>
          )
        )}

        {tabValue === "mtd-chart" && (
          <MTDChart
            key="mtd-chart"
            handleDisableTab={handleDisableTab}
            month={selectedMonth?.value}
            cancelScope="sales-pnf/forecast-report/mtd-chart"
          />
        )}

        {["product-category", "gyr-risk", "dealer-segment", "dealer-risk", "account-owner", "account-owner-risk"].includes(tabValue) && (
          <AggregatedView
            key={tabValue}
            handleDisableTab={handleDisableTab}
            viewType={tabValue}
            month={selectedMonth?.value}
          />
        )}

        {tabValue === "insights" && (
          <InsightsView
            key="insights"
            handleDisableTab={handleDisableTab}
            month={selectedMonth?.value}
            snapshotDaysMap={snapshotDaysMap}
          />
        )}

        {tabValue === "wmape" && (
          <WMAPEView
            key="wmape"
            handleDisableTab={handleDisableTab}
          />
        )}

        {/* {["product-category"].includes(tabValue) && (
          <div className="flex justify-center items-center py-10">
            <div className="px-4 py-2 rounded-full bg-yellow-100 text-yellow-700 text-sm font-medium border border-yellow-200 shadow-sm">
              🚧 To Be Done
            </div>
          </div>
        )} */}

      </Box>

      {/* Dealer Analysis Modal */}
      <DrilldownDialog dragHandleSelector=".header"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        dragResetKey={modalOpen ? `forecast-report-dealer-${modalDealerCode ?? ''}` : null}
        paperSx={{ width: '85vw', maxHeight: '80vh' }}
      >
        <Box
          className="drilldown-forecast-report-modal"
          sx={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box
            className="header"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2, py: 1.5,
              borderBottom: '1px solid #e0e0e0',
            }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#215093' }}>
              Dealer Analysis — {modalDealerCode}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <DownloadExcelButton onClick={handleModalExport} loading={modalExporting} spinnerSize={16} />
              <IconButton size="small" onClick={() => setModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <style>{`
              td:has(.row-highlight) { background: #DBD9D9 !important; }
              td:has(.row-bottom-line) { border-bottom: 1px solid !important; }
            `}</style>
            {modalLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : modalRows.length > 0 ? (
              <div className="jklc-table-wrapper dealer-analysis-modal-table">
                <TableComponent
                  columns={modalHeaders}
                  data={modalRows}
                  tablePageSize={100}
                  hidePagination
                />
              </div>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No data available
              </Typography>
            )}
          </Box>
        </Box>
      </DrilldownDialog>
    </Box >
  );
};

export default ForecastReport;
